import {useState, useEffect} from 'react';

import useParseURL from 'hooks/useParseURL';
import useIsMounted from 'hooks/useIsMounted';

import InputActionButtons from './InputActionButtons';
import backendCommunicator from 'services/backendCommunicator';
import {useInputMenuContext} from 'context/InputMenu';

import {inputMenuActionType, SWRKeyEnum} from 'interfaces';
import Alert from 'helpers/Alert';

import {type GrowthModelOverride} from 'client';
import {type AxiosError} from 'axios';
import {useGetPlanFishTypes, useGetPlanGrowthModels, useGetSites} from 'hooks/swr';
import {mutate} from 'swr';
import {GenericSelect, GenericSelectItem} from 'components/Generic/Selectors';
import {useTranslation} from 'react-i18next';
import {StyledSpacerRow} from 'styles/InputMenu';

const AddSiteGrowthModelOverride = () => {
  const {planId} = useParseURL();
  const {t} = useTranslation('common');

  const isMounted = useIsMounted();
  const [createUpdateGrowthModel, setCreateUpdateGrowthModel] = useState<boolean>(false);
  const [growthModelOverride, setGrowthModelOverride] = useState<GrowthModelOverride>({
    siteId: '',
    growthModelId: '',
    fishTypeId: '',
    id: '',
  });
  const inputMenuContext = useInputMenuContext();
  const inputMenuState = inputMenuContext?.state;
  const inputMenuDispatch = inputMenuContext?.dispatch;

  const {data: sites} = useGetSites({resourceId: planId});
  const {data: fishTypes} = useGetPlanFishTypes({resourceId: planId});
  const {data: growthModels} = useGetPlanGrowthModels({resourceId: planId});

  useEffect(() => {
    if (createUpdateGrowthModel) {
      backendCommunicator
        .createSiteGrowthModelOverride(growthModelOverride)
        .then((response) => {
          if (isMounted.current) {
            mutate([planId, SWRKeyEnum.GET_PLAN_SITE_GROWTH_MODEL_ASSIGNMENT]);
            inputMenuState?.onClose && inputMenuState.onClose(response.data);
            inputMenuDispatch && inputMenuDispatch({type: inputMenuActionType.closeMenu});
          }
        })
        .catch((error: AxiosError) => {
          isMounted.current && Alert.error(error);
        })
        .finally(() => {
          isMounted.current && setCreateUpdateGrowthModel(false);
        });
    }
  }, [createUpdateGrowthModel]);

  return (
    <form noValidate autoComplete="off" style={{width: '100%'}}>
      {/* Site Picker */}
      <GenericSelect
        value={growthModelOverride?.siteId}
        onChange={(event) => {
          const siteId = event.target.value as string;
          setGrowthModelOverride({...growthModelOverride, siteId});
        }}
        label={t('site')}
        data-testid="site"
      >
        {sites?.map((site) => (
          <GenericSelectItem key={site.id} value={site.id}>
            {site.name}
          </GenericSelectItem>
        ))}
      </GenericSelect>
      <StyledSpacerRow />

      {/* Fish Type Picker */}
      <GenericSelect
        value={growthModelOverride?.fishTypeId}
        onChange={(event) => {
          const fishTypeId = event.target.value as string;
          setGrowthModelOverride({...growthModelOverride, fishTypeId});
        }}
        label={t('fishType')}
        data-testid="fish-type"
      >
        {fishTypes?.map((fishType) => (
          <GenericSelectItem key={fishType.id} value={fishType.id}>
            {fishType.name}
          </GenericSelectItem>
        ))}
      </GenericSelect>
      <StyledSpacerRow />

      {growthModels && (
        <GenericSelect
          value={growthModelOverride.growthModelId}
          onChange={(event) => {
            const growthModelId = event.target.value as string;
            setGrowthModelOverride({...growthModelOverride, growthModelId});
          }}
          label={t('growthModel')}
          data-testid="growth-model"
        >
          {growthModels.map((growthModel) => (
            <GenericSelectItem key={growthModel.id} value={growthModel.id}>
              {growthModel.name}
            </GenericSelectItem>
          ))}
        </GenericSelect>
      )}

      <StyledSpacerRow />
      <StyledSpacerRow />

      <InputActionButtons setCreateUpdateInput={setCreateUpdateGrowthModel} />
    </form>
  );
};

export default AddSiteGrowthModelOverride;
